import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './POSubmitted.css';
import API_BASE_URL from '../config';
import { useAuth } from '../context/AuthContext';

function POSubmitted() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const canDelete = user?.email === 'narit.s@clickbroker.co.th';

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    po_number: '',
    citizen_id: '',
    noti_number: '',
    car_number: ''
  });

  // Selected submission for detail view
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Selected items for bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'single'|'bulk', id?: number }

  // QR Payment modal
  const [qrPaymentModal, setQrPaymentModal] = useState(false);
  const [qrPaymentLoading, setQrPaymentLoading] = useState(false);
  const [qrPaymentError, setQrPaymentError] = useState('');
  const [qrPaymentData, setQrPaymentData] = useState(null);
  const [qrPaymentMode, setQrPaymentMode] = useState('CURRENT'); // 'FULL' or 'CURRENT'

  // Payment Period Status modal
  const [periodStatusModal, setPeriodStatusModal] = useState(false);
  const [periodStatusLoading, setPeriodStatusLoading] = useState(false);
  const [periodStatusError, setPeriodStatusError] = useState('');
  const [periodStatusData, setPeriodStatusData] = useState([]); // Now an array

  // Fetch submissions
  const fetchSubmissions = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      // Add filters
      if (filters.status) params.append('status', filters.status);
      if (filters.po_number) params.append('po_number', filters.po_number);
      if (filters.citizen_id) params.append('citizen_id', filters.citizen_id);
      if (filters.noti_number) params.append('noti_number', filters.noti_number);
      if (filters.car_number) params.append('car_number', filters.car_number);

      // Use new submission-details API endpoint
      const response = await axios.get(`${API_BASE_URL}/api/po/submission-details?${params}`);

      if (response.data.success) {
        setSubmissions(response.data.data || []);
        setPagination(response.data.pagination);
      } else {
        setError(response.data.error || 'Failed to load submissions');
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, filters]);

  // Initial load
  useEffect(() => {
    fetchSubmissions(1);
  }, [fetchSubmissions]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchSubmissions(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      po_number: '',
      citizen_id: '',
      noti_number: '',
      car_number: ''
    });
    // Will trigger useEffect to refetch
  };

  // Page change
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchSubmissions(page);
    }
  };

  // Format date - convert to Thailand timezone (UTC+7)
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Supabase returns timestamps without timezone suffix (e.g., "2025-12-16 06:01:43.408164")
      // Normalize to ISO format and treat as UTC to ensure consistent parsing
      let normalizedStr = dateStr;
      if (!dateStr.includes('Z') && !dateStr.includes('+')) {
        normalizedStr = dateStr.replace(' ', 'T') + 'Z';
      }
      const date = new Date(normalizedStr);

      // Use Intl.DateTimeFormat for consistent timezone handling
      const formatter = new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Bangkok'
      });
      return formatter.format(date);
    } catch {
      return dateStr;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
      case 'pending':
        return 'status-pending';
      case 'approved':
      case 'success':
        return 'status-success';
      case 'rejected':
      case 'failed':
        return 'status-error';
      case 'processing':
        return 'status-processing';
      default:
        return 'status-default';
    }
  };

  // View submission detail
  const viewDetail = (submission) => {
    setSelectedSubmission(submission);
  };

  // Close detail modal
  const closeDetail = () => {
    setSelectedSubmission(null);
  };

  // Toggle single item selection
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle all items on current page
  const toggleAllSelection = () => {
    if (selectedIds.size === submissions.length && submissions.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map(s => s.id)));
    }
  };

  // Show delete confirmation
  const confirmDelete = (type, id = null) => {
    setDeleteConfirm({ type, id });
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // Delete single submission
  const deleteSingle = async (id) => {
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/po/submission-details/${id}`);
      if (response.data.success) {
        // Remove from selected if present
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        // Refresh list
        fetchSubmissions(pagination.page);
      } else {
        setError(response.data.error || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting submission:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // Delete multiple submissions
  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;

    setDeleting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/po/submission-details/bulk-delete`, {
        ids: Array.from(selectedIds)
      });
      if (response.data.success) {
        setSelectedIds(new Set());
        // Refresh list
        fetchSubmissions(1);
      } else {
        setError(response.data.error || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting submissions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // Execute delete based on confirmation type
  const executeDelete = () => {
    if (deleteConfirm?.type === 'single') {
      deleteSingle(deleteConfirm.id);
    } else if (deleteConfirm?.type === 'bulk') {
      deleteSelected();
    }
  };

  // Open QR Payment modal
  const openQrPaymentModal = () => {
    setQrPaymentModal(true);
    setQrPaymentError('');
    setQrPaymentData(null);
  };

  // Close QR Payment modal
  const closeQrPaymentModal = () => {
    setQrPaymentModal(false);
    setQrPaymentError('');
    setQrPaymentData(null);
    setQrPaymentMode('CURRENT');
  };

  // Fetch QR Payment from API
  const fetchQrPayment = async () => {
    if (!selectedSubmission) return;

    setQrPaymentLoading(true);
    setQrPaymentError('');
    setQrPaymentData(null);

    try {
      // Use ame_transaction_id if available, otherwise use noti_number (PO_ID)
      const response = await axios.post(`${API_BASE_URL}/api/po/qr-payment`, {
        transaction_id: selectedSubmission.ame_transaction_id || null,
        noti_number: selectedSubmission.noti_number || selectedSubmission.po_number,
        payment_mode: qrPaymentMode
      });

      if (response.data.success) {
        setQrPaymentData(response.data.data);
      } else {
        setQrPaymentError(response.data.error || 'ไม่สามารถดึง QR Payment ได้');
      }
    } catch (err) {
      console.error('Error fetching QR payment:', err);
      setQrPaymentError(err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
    } finally {
      setQrPaymentLoading(false);
    }
  };

  // Open Payment Period modal
  const openPeriodStatusModal = () => {
    setPeriodStatusModal(true);
    setPeriodStatusError('');
    setPeriodStatusData([]);
    // Auto fetch when opening
    fetchPeriodStatus();
  };

  // Close Payment Period modal
  const closePeriodStatusModal = () => {
    setPeriodStatusModal(false);
    setPeriodStatusError('');
    setPeriodStatusData([]);
  };

  // Fetch Payment Period Status (Batch)
  const fetchPeriodStatus = async () => {
    if (!selectedSubmission) return;

    // Check if installments exist
    if (!selectedSubmission.number_installment || parseInt(selectedSubmission.number_installment) < 2) {
      setPeriodStatusError('รายการนี้ไม่มีจำนวนงวดที่ต้องตรวจสอบ (น้อยกว่า 2 งวด)');
      return;
    }

    setPeriodStatusLoading(true);
    setPeriodStatusError('');
    setPeriodStatusData([]);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/po/period-status`, {
        transaction_id: selectedSubmission.ame_transaction_id || null,
        noti_number: selectedSubmission.noti_number || selectedSubmission.po_number,
        number_installment: parseInt(selectedSubmission.number_installment)
      });

      if (response.data.success) {
        setPeriodStatusData(response.data.data || []);
      } else {
        setPeriodStatusError(response.data.error || 'ไม่สามารถตรวจสอบสถานะงวดได้');
      }
    } catch (err) {
      console.error('Error fetching period status:', err);
      setPeriodStatusError(err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
    } finally {
      setPeriodStatusLoading(false);
    }
  };

  return (
    <div className="po-submitted">
      <h2>PO Submitted (รายการที่ส่งแล้ว)</h2>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="processing">Processing</option>
            </select>
          </div>

          <div className="filter-group">
            <label>PO Number</label>
            <input
              type="text"
              value={filters.po_number}
              onChange={(e) => handleFilterChange('po_number', e.target.value)}
              placeholder="Search PO..."
            />
          </div>

          <div className="filter-group">
            <label>Citizen ID</label>
            <input
              type="text"
              value={filters.citizen_id}
              onChange={(e) => handleFilterChange('citizen_id', e.target.value)}
              placeholder="Search ID..."
            />
          </div>

          <div className="filter-group">
            <label>Car Number</label>
            <input
              type="text"
              value={filters.car_number}
              onChange={(e) => handleFilterChange('car_number', e.target.value)}
              placeholder="Search chassis..."
            />
          </div>

          <div className="filter-actions">
            <button className="btn-search" onClick={applyFilters}>
              Search
            </button>
            <button className="btn-clear" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="loading-indicator">
          Loading...
        </div>
      )}

      {/* Results Table */}
      {!loading && !error && (
        <>
          <div className="results-toolbar">
            <div className="results-info">
              Showing {submissions.length} of {pagination.total} submissions (Page {pagination.page}/{pagination.totalPages || 1})
            </div>
            {selectedIds.size > 0 && canDelete && (
              <button
                className="btn-bulk-delete"
                onClick={() => confirmDelete('bulk')}
                disabled={deleting}
              >
                Delete Selected ({selectedIds.size})
              </button>
            )}
          </div>

          <div className="table-container">
            <table className="submissions-table">
              <thead>
                <tr>
                  {canDelete && (
                    <th className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === submissions.length && submissions.length > 0}
                        onChange={toggleAllSelection}
                      />
                    </th>
                  )}
                  <th>#</th>
                  <th>PO Number</th>
                  <th>Customer</th>
                  <th>Citizen ID</th>
                  <th>Car</th>
                  <th>Insurance</th>
                  <th>Net Price</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.length === 0 ? (
                  <tr>
                    <td colSpan={canDelete ? "11" : "10"} className="no-data">
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  submissions.map((sub, idx) => (
                    <tr key={sub.id} className={selectedIds.has(sub.id) ? 'selected' : ''}>
                      {canDelete && (
                        <td className="col-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(sub.id)}
                            onChange={() => toggleSelection(sub.id)}
                          />
                        </td>
                      )}
                      <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                      <td className="po-number">{sub.po_number || '-'}</td>
                      <td>{sub.customer_name || '-'}</td>
                      <td className="mono">{sub.citizen_id || '-'}</td>
                      <td>
                        <div className="car-info">
                          <span className="car-brand">{sub.car_brand_name || '-'}</span>
                          <span className="car-model">{sub.car_model_name || ''}</span>
                        </div>
                      </td>
                      <td>{sub.insurance_company_name || '-'}</td>
                      <td className="price">{formatCurrency(sub.net_price)}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(sub.status)}`}>
                          {sub.status || '-'}
                        </span>
                      </td>
                      <td className="date">{formatDate(sub.submitted_at)}</td>
                      <td className="actions-cell">
                        <button
                          className="btn-view"
                          onClick={() => viewDetail(sub)}
                        >
                          View
                        </button>
                        {canDelete && (
                          <button
                            className="btn-delete"
                            onClick={() => confirmDelete('single', sub.id)}
                            disabled={deleting}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(1)}
              >
                First
              </button>
              <button
                className="page-btn"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
              >
                Prev
              </button>

              <div className="page-numbers">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      className={`page-num ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => goToPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                className="page-btn"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.page + 1)}
              >
                Next
              </button>
              <button
                className="page-btn"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.totalPages)}
              >
                Last
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Submission Detail</h3>
              <button className="btn-close" onClick={closeDetail}>&times;</button>
            </div>
            <div className="modal-body">
              {/* General & AME Response */}
              <div className="detail-section">
                <h4>📌 General</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>PO Number:</label>
                    <span>{selectedSubmission.po_number || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Noti Number:</label>
                    <span>{selectedSubmission.noti_number || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>AME Status:</label>
                    <span className={`status-badge ${getStatusClass(selectedSubmission.ame_status)}`}>
                      {selectedSubmission.ame_status || '-'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>AME Transaction ID:</label>
                    <span>{selectedSubmission.ame_transaction_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Customer Car Insurance ID:</label>
                    <span>{selectedSubmission.ame_customer_car_insurance_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Submitted:</label>
                    <span>{formatDate(selectedSubmission.submitted_at)}</span>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="detail-section">
                <h4>👤 Customer</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{selectedSubmission.title} {selectedSubmission.first_name} {selectedSubmission.last_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Citizen ID:</label>
                    <span className="mono">{selectedSubmission.citizen_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Gender:</label>
                    <span>{selectedSubmission.gender === 0 ? 'ชาย' : selectedSubmission.gender === 1 ? 'หญิง' : '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Birth Date:</label>
                    <span>{selectedSubmission.birth_date || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Mobile:</label>
                    <span>{selectedSubmission.mobile_number || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{selectedSubmission.phone_number || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="detail-section">
                <h4>📍 Address</h4>
                <div className="detail-grid">
                  <div className="detail-item full-width">
                    <label>Address:</label>
                    <span>{selectedSubmission.address || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Province:</label>
                    <span>{selectedSubmission.address_province || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>District:</label>
                    <span>{selectedSubmission.address_district || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Sub-district:</label>
                    <span>{selectedSubmission.address_sub_district || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Postcode:</label>
                    <span>{selectedSubmission.postcode || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Car Information */}
              <div className="detail-section">
                <h4>🚗 Car</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Brand:</label>
                    <span>{selectedSubmission.car_brand_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Model:</label>
                    <span>{selectedSubmission.car_model_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Reg Year:</label>
                    <span>{selectedSubmission.reg_year || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Chassis:</label>
                    <span className="mono">{selectedSubmission.chassis_number || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Plate:</label>
                    <span className="mono">{selectedSubmission.plate_number || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Engine:</label>
                    <span className="mono">{selectedSubmission.engine_number || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Insurance Information */}
              <div className="detail-section">
                <h4>🏢 Insurance</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{selectedSubmission.insurance_company_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Car Type:</label>
                    <span>{selectedSubmission.insurance_car_type_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Product:</label>
                    <span>{selectedSubmission.product_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Insurance Number:</label>
                    <span>{selectedSubmission.insurance_number || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>ไฟล์ภาพใบแจ้งงาน:</label>
                    {selectedSubmission.insurance_number_url ? (
                      <a
                        href={selectedSubmission.insurance_number_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-link"
                      >
                        📎 ดูไฟล์
                      </a>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Purchase Date:</label>
                    <span>{selectedSubmission.purchase_date || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Effective:</label>
                    <span>{selectedSubmission.effective_date || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Expiry:</label>
                    <span>{selectedSubmission.expiry_date || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Price Information */}
              <div className="detail-section">
                <h4>💰 Price</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Premium Price:</label>
                    <span className="price">{formatCurrency(selectedSubmission.premium_price)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Premium Real Price:</label>
                    <span className="price">{formatCurrency(selectedSubmission.premium_real_price)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Net Price:</label>
                    <span className="price">{formatCurrency(selectedSubmission.net_price)}</span>
                  </div>
                </div>
              </div>

              {/* Installment Details - Direct from table columns */}
              {(selectedSubmission.first_payment || selectedSubmission.number_installment) && (
                <div className="detail-section">
                  <h4>💳 รายละเอียดการผ่อนชำระ</h4>
                  <div className="detail-grid">
                    <div className="detail-item highlight">
                      <label>งวดแรก:</label>
                      <span className="price highlight-value">
                        {formatCurrency(selectedSubmission.first_payment)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>จำนวนงวด:</label>
                      <span>{selectedSubmission.number_installment || '-'} งวด</span>
                    </div>
                    <div className="detail-item highlight">
                      <label>ยอดผ่อน/งวด:</label>
                      <span className="price highlight-value">
                        {formatCurrency(selectedSubmission.payment_installment)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>ประเภทการชำระ:</label>
                      <span>{selectedSubmission.payment_type || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <label>เปอร์เซ็นต์ดาวน์:</label>
                      <span>{selectedSubmission.down_payment_percent || 0}%</span>
                    </div>
                    <div className="detail-item">
                      <label>ค่าบริการ:</label>
                      <span>{formatCurrency(selectedSubmission.service_price)}</span>
                    </div>
                    <div className="detail-item">
                      <label>เบี้ยที่ใช้ค้นหา:</label>
                      <span>{formatCurrency(selectedSubmission.premium_used_for_lookup)}</span>
                    </div>
                  </div>

                  {/* QR Payment Button */}
                  <div className="qr-payment-section">
                    <button
                      className="btn-qr-payment"
                      onClick={openQrPaymentModal}
                    >
                      📱 QR Payment (ชำระเงิน)
                    </button>
                    <button
                      className="btn-period-status"
                      onClick={openPeriodStatusModal}
                      style={{ marginLeft: '10px', backgroundColor: '#4CAF50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      📅 งวดชำระ
                    </button>
                  </div>
                </div>
              )}

              {/* AME Response */}
              {selectedSubmission.ame_response_data && (
                <div className="detail-section">
                  <h4>📡 AME Response</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Code:</label>
                      <span className="mono">{selectedSubmission.ame_code || '-'}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Message:</label>
                      <span>{selectedSubmission.ame_message || '-'}</span>
                    </div>
                  </div>
                  <pre className="json-display">
                    {JSON.stringify(selectedSubmission.ame_response_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="btn-close" onClick={cancelDelete}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="confirm-message">
                {deleteConfirm.type === 'single'
                  ? 'Are you sure you want to delete this submission?'
                  : `Are you sure you want to delete ${selectedIds.size} selected submission(s)?`}
              </p>
              <p className="confirm-warning">This action cannot be undone.</p>
              <div className="confirm-actions">
                <button className="btn-cancel" onClick={cancelDelete} disabled={deleting}>
                  Cancel
                </button>
                <button className="btn-confirm-delete" onClick={executeDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Payment Modal */}
      {qrPaymentModal && (
        <div className="modal-overlay" onClick={closeQrPaymentModal}>
          <div className="modal-content qr-payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📱 QR Payment</h3>
              <button className="btn-close" onClick={closeQrPaymentModal}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Payment Mode Selection */}
              <div className="payment-mode-section">
                <label>เลือกประเภทการชำระ:</label>
                <div className="payment-mode-options">
                  <label className={`payment-mode-option ${qrPaymentMode === 'CURRENT' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMode"
                      value="CURRENT"
                      checked={qrPaymentMode === 'CURRENT'}
                      onChange={(e) => setQrPaymentMode(e.target.value)}
                    />
                    <span className="option-label">ยอดค้างงวดล่าสุด</span>
                    <span className="option-desc">ชำระเฉพาะงวดปัจจุบัน</span>
                  </label>
                  <label className={`payment-mode-option ${qrPaymentMode === 'FULL' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMode"
                      value="FULL"
                      checked={qrPaymentMode === 'FULL'}
                      onChange={(e) => setQrPaymentMode(e.target.value)}
                    />
                    <span className="option-label">ยอดปิดชำระทั้งหมด</span>
                    <span className="option-desc">ชำระเต็มจำนวนที่เหลือ</span>
                  </label>
                </div>
                <button
                  className="btn-get-qr"
                  onClick={fetchQrPayment}
                  disabled={qrPaymentLoading}
                >
                  {qrPaymentLoading ? '⏳ กำลังโหลด...' : '🔍 ดึง QR Code'}
                </button>
              </div>

              {/* Error Message */}
              {qrPaymentError && (
                <div className="qr-error-message">
                  <span className="error-icon">❌</span>
                  <span>{qrPaymentError}</span>
                </div>
              )}

              {/* QR Payment Result */}
              {qrPaymentData && (
                <div className="qr-result-section">
                  <div className="qr-image-container">
                    <img
                      src={qrPaymentData.qr_path}
                      alt="QR Payment"
                      className="qr-image"
                    />
                  </div>
                  <div className="qr-details">
                    <div className="qr-amount">
                      <label>ยอดที่ต้องชำระ:</label>
                      <span className="amount-value">
                        {new Intl.NumberFormat('th-TH', {
                          style: 'currency',
                          currency: 'THB'
                        }).format(qrPaymentData.amount || 0)}
                      </span>
                    </div>
                    {qrPaymentData.reference1 && (
                      <div className="qr-reference">
                        <label>Reference 1:</label>
                        <span>{qrPaymentData.reference1}</span>
                      </div>
                    )}
                    {qrPaymentData.reference2 && (
                      <div className="qr-reference">
                        <label>Reference 2:</label>
                        <span>{qrPaymentData.reference2}</span>
                      </div>
                    )}
                  </div>
                  <p className="qr-instruction">
                    สแกน QR Code ด้วยแอพธนาคารเพื่อชำระเงิน
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Period Status Modal */}
      {periodStatusModal && (
        <div className="modal-overlay" onClick={closePeriodStatusModal}>
          <div className="modal-content qr-payment-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h3>📅 ข้อมูลสถานะงวด (Payment Period Status)</h3>
              <button className="btn-close" onClick={closePeriodStatusModal}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Header Info */}
              <div className="period-header-info" style={{ marginBottom: '15px' }}>
                <div><strong>Noti Number:</strong> {selectedSubmission?.noti_number || selectedSubmission?.po_number}</div>
                <div><strong>Total Installments:</strong> {selectedSubmission?.number_installment}</div>
              </div>

              {/* Loading */}
              {periodStatusLoading && (
                <div className="loading-indicator" style={{ padding: '20px', textAlign: 'center' }}>
                  ⏳ กำลังตรวจสอบสถานะงวดทั้งหมด...
                </div>
              )}

              {/* Error Message */}
              {periodStatusError && (
                <div className="qr-error-message">
                  <span className="error-icon">❌</span>
                  <span>{periodStatusError}</span>
                </div>
              )}

              {/* Results Table */}
              {!periodStatusLoading && periodStatusData.length > 0 && (
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="submissions-table">
                    <thead>
                      <tr>
                        <th>งวดที่</th>
                        <th>วันครบกำหนด</th>
                        <th>วันที่รับชำระ</th>
                        <th>ยอดเงิน</th>
                        <th>ค่าธรรมเนียม</th>
                        <th>สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodStatusData.map((period, idx) => (
                        <tr key={idx} className={period.status === 'ERROR' ? 'row-error' : ''}>
                          <td>{period.period_no || '-'}</td>
                          <td>{period.payment_due_date || '-'}</td>
                          <td>{period.payment_received_date || '-'}</td>
                          <td className="price">{period.amount ? formatCurrency(period.amount) : '-'}</td>
                          <td className="price">{period.fine ? formatCurrency(period.fine) : '-'}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(period.status)}`}>
                              {period.status || '-'}
                            </span>
                            {period.error_message && (
                              <div style={{ fontSize: '0.8em', color: 'red' }}>{period.error_message}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!periodStatusLoading && !periodStatusError && periodStatusData.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  ไม่พบข้อมูลหรืองวดชำระ
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default POSubmitted;
